/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { ClientInvoiceExportAcceptedResult } from '../models/client-invoice-export-accepted-result';
import { ClientInvoiceExportDetail } from '../models/client-invoice-export-detail';
import { ClientInvoiceExportRequest } from '../models/client-invoice-export-request';
import { ClientInvoiceExportSummary } from '../models/client-invoice-export-summary';
import { Error } from '../models/error';


/**
 * Downloadable export artifacts (client invoice PDF bundles). Generic
 * substrate for "produce a downloadable zip / report" workflows.
 */
@Injectable({
  providedIn: 'root',
})
export class ExportsService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation listClientInvoiceExports
   */
  static readonly ListClientInvoiceExportsPath = '/clients/{client_id}/exports';

  /**
   * List a client's exports, newest first.
   *
   * Returns this client's export rows ordered by `requested_at DESC`.
   * Includes terminal-state rows (`expired`, `failed`) so the Console
   * can show the historical audit trail.
   *
   * Requires the `exports:client-invoice` permission (granted to the
   * `User` and `Administrator` roles).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listClientInvoiceExports()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClientInvoiceExports$Response(params: {
    client_id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<{
'exports': Array<ClientInvoiceExportSummary>;
}>> {

    const rb = new RequestBuilder(this.rootUrl, ExportsService.ListClientInvoiceExportsPath, 'get');
    if (params) {
      rb.path('client_id', params.client_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<{
        'exports': Array<ClientInvoiceExportSummary>;
        }>;
      })
    );
  }

  /**
   * List a client's exports, newest first.
   *
   * Returns this client's export rows ordered by `requested_at DESC`.
   * Includes terminal-state rows (`expired`, `failed`) so the Console
   * can show the historical audit trail.
   *
   * Requires the `exports:client-invoice` permission (granted to the
   * `User` and `Administrator` roles).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listClientInvoiceExports$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listClientInvoiceExports(params: {
    client_id: number;
    context?: HttpContext
  }
): Observable<{
'exports': Array<ClientInvoiceExportSummary>;
}> {

    return this.listClientInvoiceExports$Response(params).pipe(
      map((r: StrictHttpResponse<{
'exports': Array<ClientInvoiceExportSummary>;
}>) => r.body as {
'exports': Array<ClientInvoiceExportSummary>;
})
    );
  }

  /**
   * Path part for operation createClientInvoiceExport
   */
  static readonly CreateClientInvoiceExportPath = '/clients/{client_id}/exports';

  /**
   * Start a client-invoice PDF export.
   *
   * Starts a ClientInvoiceBundleWorkflow that resolves InvoiceAttachments
   * for the given client + date range + scope set, zips them into a
   * single archive (with `manifest.csv` at the root), and stores it in
   * Azure Blob storage with a 7-day TTL. The endpoint inserts a `pending`
   * row into `exports` and starts the workflow; on Temporal start failure
   * the row is deleted, so the API does not leave orphaned pending rows.
   *
   * Requires the `exports:client-invoice` permission (granted to the
   * `User` and `Administrator` roles).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createClientInvoiceExport()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClientInvoiceExport$Response(params: {
    client_id: number;
    context?: HttpContext
    body: ClientInvoiceExportRequest
  }
): Observable<StrictHttpResponse<ClientInvoiceExportAcceptedResult>> {

    const rb = new RequestBuilder(this.rootUrl, ExportsService.CreateClientInvoiceExportPath, 'post');
    if (params) {
      rb.path('client_id', params.client_id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientInvoiceExportAcceptedResult>;
      })
    );
  }

  /**
   * Start a client-invoice PDF export.
   *
   * Starts a ClientInvoiceBundleWorkflow that resolves InvoiceAttachments
   * for the given client + date range + scope set, zips them into a
   * single archive (with `manifest.csv` at the root), and stores it in
   * Azure Blob storage with a 7-day TTL. The endpoint inserts a `pending`
   * row into `exports` and starts the workflow; on Temporal start failure
   * the row is deleted, so the API does not leave orphaned pending rows.
   *
   * Requires the `exports:client-invoice` permission (granted to the
   * `User` and `Administrator` roles).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createClientInvoiceExport$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClientInvoiceExport(params: {
    client_id: number;
    context?: HttpContext
    body: ClientInvoiceExportRequest
  }
): Observable<ClientInvoiceExportAcceptedResult> {

    return this.createClientInvoiceExport$Response(params).pipe(
      map((r: StrictHttpResponse<ClientInvoiceExportAcceptedResult>) => r.body as ClientInvoiceExportAcceptedResult)
    );
  }

  /**
   * Path part for operation getExport
   */
  static readonly GetExportPath = '/exports/{export_id}';

  /**
   * Get a single export's full row.
   *
   * Returns the full detail for one export. Allowed for the original
   * `requested_by` user or any caller holding the
   * `exports:client-invoice` permission — the requester branch lets
   * the originator still fetch their own results after the granting
   * role is revoked.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getExport()` instead.
   *
   * This method doesn't expect any request body.
   */
  getExport$Response(params: {
    export_id: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<ClientInvoiceExportDetail>> {

    const rb = new RequestBuilder(this.rootUrl, ExportsService.GetExportPath, 'get');
    if (params) {
      rb.path('export_id', params.export_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ClientInvoiceExportDetail>;
      })
    );
  }

  /**
   * Get a single export's full row.
   *
   * Returns the full detail for one export. Allowed for the original
   * `requested_by` user or any caller holding the
   * `exports:client-invoice` permission — the requester branch lets
   * the originator still fetch their own results after the granting
   * role is revoked.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getExport$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getExport(params: {
    export_id: string;
    context?: HttpContext
  }
): Observable<ClientInvoiceExportDetail> {

    return this.getExport$Response(params).pipe(
      map((r: StrictHttpResponse<ClientInvoiceExportDetail>) => r.body as ClientInvoiceExportDetail)
    );
  }

  /**
   * Path part for operation downloadExport
   */
  static readonly DownloadExportPath = '/exports/{export_id}/download';

  /**
   * Redirect to a short-lived signed URL for the export zip.
   *
   * Returns HTTP 302 with `Location` set to a 5-minute signed Azure URL.
   * Allowed for the original `requested_by` user or any caller holding
   * the `exports:client-invoice` permission — the requester branch lets
   * the originator still fetch their own results after the granting
   * role is revoked. Each successful 302 emits one structured audit log
   * entry recording `(export_id, downloaded_by_user_id, downloaded_at)`.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `downloadExport()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadExport$Response(params: {
    export_id: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Error>> {

    const rb = new RequestBuilder(this.rootUrl, ExportsService.DownloadExportPath, 'get');
    if (params) {
      rb.path('export_id', params.export_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Error>;
      })
    );
  }

  /**
   * Redirect to a short-lived signed URL for the export zip.
   *
   * Returns HTTP 302 with `Location` set to a 5-minute signed Azure URL.
   * Allowed for the original `requested_by` user or any caller holding
   * the `exports:client-invoice` permission — the requester branch lets
   * the originator still fetch their own results after the granting
   * role is revoked. Each successful 302 emits one structured audit log
   * entry recording `(export_id, downloaded_by_user_id, downloaded_at)`.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `downloadExport$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadExport(params: {
    export_id: string;
    context?: HttpContext
  }
): Observable<Error> {

    return this.downloadExport$Response(params).pipe(
      map((r: StrictHttpResponse<Error>) => r.body as Error)
    );
  }

}
